import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BrandCategoryPanel = ({
  brands = [],
  categories = [],
  onAddBrand,
  onEditBrand,
  onDeleteBrand,
  onAddCategory,
  onEditCategory,
  onDeleteCategory
}) => {
  const [activeTab, setActiveTab] = useState('brands');

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Settings" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Brand & Category Management</h3>
            <p className="text-sm text-muted-foreground">Manage brands and categories</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setActiveTab('brands')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'brands' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Icon name="Tag" size={16} />
            Brands ({brands?.length})
          </div>
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'categories' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Icon name="Folder" size={16} />
            Categories ({categories?.length})
          </div>
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {activeTab === 'brands' ? (
          <div>
            {/* Add Brand Button */}
            <Button
              variant="outline"
              iconName="Plus"
              iconPosition="left"
              onClick={onAddBrand}
              className="w-full mb-4"
            >
              Add Brand
            </Button>

            {/* Brands List */}
            <div className="space-y-3">
              {brands?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="Tag" size={32} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm">No brands yet</p>
                  <p className="text-muted-foreground text-xs">Add your first brand to get started</p>
                </div>
              ) : (
                brands?.map(brand => (
                  <div
                    key={brand?.id}
                    className="p-3 border border-border rounded-lg hover:bg-accent/5 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-foreground">{brand?.name}</h4>
                          <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded font-mono">
                            {brand?.code}
                          </span>
                        </div>
                        {brand?.description && (
                          <p className="text-xs text-muted-foreground mt-1">{brand?.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEditBrand(brand)}
                          className="h-8 w-8"
                        >
                          <Icon name="Edit2" size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDeleteBrand(brand)}
                          className="h-8 w-8 text-destructive hover:text-destructive"
                        >
                          <Icon name="Trash2" size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div>
            {/* Add Category Button */}
            <Button
              variant="outline"
              iconName="Plus"
              iconPosition="left"
              onClick={onAddCategory}
              className="w-full mb-4"
            >
              Add Category
            </Button>

            {/* Categories List */}
            <div className="space-y-3">
              {categories?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="Folder" size={32} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm">No categories yet</p>
                  <p className="text-muted-foreground text-xs">Add your first category to organize visuals</p>
                </div>
              ) : (
                categories?.map(category => (
                  <div
                    key={category?.id}
                    className="p-3 border border-border rounded-lg hover:bg-accent/5 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-foreground">{category?.name}</h4>
                          <span className="px-2 py-1 bg-secondary/10 text-secondary text-xs rounded font-mono">
                            {category?.code}
                          </span>
                        </div>
                        {category?.description && (
                          <p className="text-xs text-muted-foreground mt-1">{category?.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEditCategory(category)}
                          className="h-8 w-8"
                        >
                          <Icon name="Edit2" size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDeleteCategory(category)}
                          className="h-8 w-8 text-destructive hover:text-destructive"
                        >
                          <Icon name="Trash2" size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandCategoryPanel;